import { useCallback, useEffect, useRef } from "react";
import styled from "styled-components";

interface Frame {
  dx: number;
  dy: number;
}

interface AdjustmentHelperRectProps {
  frame: Frame;
  /** false 면 read-only (조정 완료 상태) — 드래그/커서 비활성, 시각적 표시만 */
  interactive?: boolean;
  onChange?: (frame: Frame) => void;
  /** 드래그 시작 시 1회 — onChange 는 드래그 내내 발생하므로 되돌리기 스냅샷은 여기서 찍는다 */
  onChangeStart?: () => void;
}

/**
 * 화각 조정 helper rect — 스냅샷 위에 동일 사이즈 사각형을 dx/dy% 만큼 평행이동.
 * 사용자가 드래그해서 카메라 화각의 framing offset 을 시각적으로 지정 (legacy CameraAdjustmentHelperShape).
 */
export function AdjustmentHelperRect({ frame, interactive = true, onChange, onChangeStart }: AdjustmentHelperRectProps) {
  const dragRef = useRef<{
    startX: number;
    startY: number;
    startDx: number;
    startDy: number;
    containerWidth: number;
    containerHeight: number;
  } | null>(null);
  const rectRef = useRef<HTMLDivElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!interactive) return;
      e.stopPropagation();
      const rect = rectRef.current;
      const container = rect?.parentElement;
      if (!rect || !container) return;
      rect.setPointerCapture(e.pointerId);
      onChangeStart?.();
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startDx: frame.dx,
        startDy: frame.dy,
        containerWidth: container.clientWidth,
        containerHeight: container.clientHeight,
      };
    },
    [interactive, frame.dx, frame.dy, onChangeStart],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!interactive) return;
      const drag = dragRef.current;
      if (!drag) return;
      const dxDeltaPct = ((e.clientX - drag.startX) / drag.containerWidth) * 100;
      const dyDeltaPct = ((e.clientY - drag.startY) / drag.containerHeight) * 100;
      onChange?.({
        dx: drag.startDx + dxDeltaPct,
        dy: drag.startDy + dyDeltaPct,
      });
    },
    [interactive, onChange],
  );

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const rect = rectRef.current;
    if (rect && rect.hasPointerCapture(e.pointerId)) {
      rect.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
  }, []);

  // 페이지 unmount 시 보호
  useEffect(() => {
    return () => {
      dragRef.current = null;
    };
  }, []);

  return (
    <Rect
      ref={rectRef}
      $interactive={interactive}
      style={{ transform: `translate(${frame.dx}%, ${frame.dy}%)` }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    />
  );
}

const Rect = styled.div<{ $interactive: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border: 3px solid #329eff;
  border-radius: 2px;
  background: rgba(50, 158, 255, 0.04);
  cursor: ${({ $interactive }) => ($interactive ? "grab" : "default")};
  touch-action: none;
  pointer-events: ${({ $interactive }) => ($interactive ? "auto" : "none")};
  box-sizing: border-box;

  &:active {
    cursor: ${({ $interactive }) => ($interactive ? "grabbing" : "default")};
  }
`;
